import { UseFormReturn, Controller } from "react-hook-form"
import { FormValues } from "./useTransitionToEnrolledForm"
import { DatePicker, FormLabel } from "@oysterhr/ui"
import { MoneyInput, NumericTextInput } from "@oysterhr/feature-components"
import { BenefitsPlan } from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import { getBenefitPlanAvailableCurrencies, extractErrorMessage } from "../../utils"
import { VerticalFormStack } from "~/shared/components/VerticalFormStack"
import { useMemo } from "react"
import { usePercentageContributionFieldController } from "../../hooks/usePercentageContributionFieldController"

export interface TransitionToEnrolledFormProps {
  form: UseFormReturn<FormValues>
  plan: BenefitsPlan
  customerContributionPercentagePreference?: number
  parentEnrollmentPriceCurrency?: string
}

export function TransitionToEnrolledForm({
  form,
  plan,
  parentEnrollmentPriceCurrency,
  customerContributionPercentagePreference,
}: TransitionToEnrolledFormProps) {
  const [t] = useTranslation("benefits/TransitionToEnrolledModal")
  const costSplitAllowed = plan.costSplitAllowed
  const { control, watch, setValue, getValues } = form

  const availableCurrencies = useMemo(() => {
    const currencies = getBenefitPlanAvailableCurrencies(plan)
    if (parentEnrollmentPriceCurrency) {
      return currencies.filter(({ currencyCode }) => currencyCode === parentEnrollmentPriceCurrency)
    }
    return currencies
  }, [plan, parentEnrollmentPriceCurrency])

  const defaultContributionPercentage = useMemo(() => {
    const [employerContribution, price] = getValues(["employerContribution", "price"])
    if (!employerContribution.trim() || !price.amount) {
      return customerContributionPercentagePreference
    }

    return (Number(employerContribution) / Number(price.amount)) * 100
  }, [getValues, customerContributionPercentagePreference])

  const {
    onChangeContributionAmount,
    onChangeContributionPercentageInput,
    onChangeTotalCostAmount,
    percentageInputValue,
  } = usePercentageContributionFieldController({
    getTotalCostAmount: () => getValues("price.amount"),
    setContributionAmount(amount) {
      setValue("employerContribution", amount)
    },
    defaultContributionPercentage,
  })
  const totalPrice = watch("price")

  return (
    <form>
      <VerticalFormStack>
        <Controller
          control={control}
          name="price"
          render={({ field: { onBlur, onChange, value, name }, fieldState: { error } }) => (
            <div data-testid="price-field">
              <MoneyInput
                currencies={availableCurrencies}
                label={<FormLabel label={t("price")} />}
                name={name}
                value={{ currency: value.currency, amount: value.amount ?? "" }}
                onChange={value => {
                  onChange(value)
                  if (costSplitAllowed) {
                    onChangeTotalCostAmount(value.amount)
                  }
                }}
                onBlur={onBlur}
                error={extractErrorMessage(error)}
              />
            </div>
          )}
        />
        {costSplitAllowed && (
          <>
            <NumericTextInput
              maxDecimalsDigits={2}
              startAdornment="%"
              name="employerContributionPercentage"
              label={
                <FormLabel
                  label={t("employerContributionPercentage")}
                  assistiveText={t("customerPreference", {
                    percentage: customerContributionPercentagePreference,
                  })}
                />
              }
              value={percentageInputValue}
              onChange={event => onChangeContributionPercentageInput(event.target.value)}
            />

            <Controller
              control={control}
              name="employerContribution"
              render={({ field: { onBlur, onChange, value, name }, fieldState: { error } }) => (
                <MoneyInput
                  currencies={availableCurrencies}
                  label={<FormLabel label={t("employerContributionAmount")} />}
                  name={name}
                  value={{ currency: totalPrice.currency, amount: value ?? "" }}
                  onChange={({ amount, currency }) => {
                    setValue("price", { currency, amount: getValues("price.amount") })
                    onChange(amount)
                    onChangeContributionAmount(amount)
                  }}
                  onBlur={onBlur}
                  error={extractErrorMessage(error)}
                />
              )}
            />
          </>
        )}
        <Controller
          control={control}
          name="coverageStartDate"
          render={({ field: { onChange, name, value, onBlur }, fieldState: { error } }) => (
            <DatePicker
              label={<FormLabel label={t("coverageStartDate")} />}
              onChange={onChange}
              value={value}
              onBlur={onBlur}
              name={name}
              error={error?.message}
            />
          )}
        />
      </VerticalFormStack>
    </form>
  )
}
